/**
 * SYSTEM MONITORING — Multi-VPS Infrastructure (8 nodi, Task #1578)
 * Dashboard real-time per FE-1, BE-1, BE-2, DB-1, BE-3, BE-4, DB-2, REDIS-1.
 * Dati aggregati via /api/master/infrastructure/health (BE-1 collector).
 */

import { useState, useEffect, useCallback } from 'react';
import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle, GlassCardDescription } from '@easyflow/ui-master/components/ui/glass-card';
import { Progress } from '@easyflow/ui-master/components/ui/progress';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import apiClient from '@/lib/api';
import { toErr } from '@easyflow/shared';
import {
  Activity,
  HardDrive,
  Cpu,
  MemoryStick,
  Network,
  Clock,
  Server,
  Zap,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Play,
  Square,
  RotateCw,
  Database,
  Globe
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

type ServerKey = 'fe1' | 'be1' | 'be2' | 'db1' | 'be3' | 'be4' | 'db2' | 'redis1';
type ServiceAction = 'start' | 'stop' | 'restart';

interface InfraServerData {
  status?: string;
  cpu?: { usage?: number; cores?: number; loadAvg?: [number, number, number] };
  memory?: { used?: number; total?: number; free?: number; percentage?: number };
  disk?: { used?: number; total?: number; free?: number; percentage?: number };
  uptime?: number;
  freeswitch?: { connected?: boolean; state?: string; latency_ms?: number };
  redis?: { connected?: boolean; latency_ms?: number };
  nginx?: { active_connections?: number; status?: string };
  db_size_gb?: string;
  active_connections?: number;
  query_latency_ms?: number;
  // BE-3/BE-4 (LiveKit nodes)
  probe?: 'http' | 'tcp' | 'none';
  latency_ms?: number | null;
  port?: number;
  detail?: string;
  // DB-2
  state?: string | null;
  sync_state?: string | null;
  lag_bytes?: number | null;
  tcp_latency_ms?: number | null;
  // REDIS-1
  ping?: 'PONG' | null;
  version?: string | null;
  used_memory_human?: string | null;
  used_memory_peak_human?: string | null;
  fragmentation_ratio?: number | null;
  connected_clients?: number | null;
  blocked_clients?: number | null;
}

interface InfraHealth {
  overall: string;
  timestamp: string;
  servers: Record<ServerKey, InfraServerData | undefined>;
}

interface HistoryPoint {
  time: string;
  cpu: number;
  memory: number;
}

const SERVER_LABELS: Record<ServerKey, { label: string; role: string; ip: string; icon: React.ReactNode; color: string }> = {
  fe1:    { label: 'FE-1',    role: 'Nginx/Gateway',         ip: '10.4.202.50', icon: <Globe    className="h-5 w-5 text-cyan-400" />,    color: 'cyan' },
  be1:    { label: 'BE-1',    role: 'Node.js + FreeSWITCH',  ip: '10.4.202.51', icon: <Zap      className="h-5 w-5 text-amber-400" />,   color: 'amber' },
  be2:    { label: 'BE-2',    role: 'Node.js API',           ip: '10.4.202.52', icon: <Server   className="h-5 w-5 text-purple-400" />,  color: 'purple' },
  db1:    { label: 'DB-1',    role: 'PostgreSQL primary',    ip: '10.4.202.53', icon: <Database className="h-5 w-5 text-emerald-400" />, color: 'emerald' },
  be3:    { label: 'BE-3',    role: 'LiveKit cluster #1',    ip: '10.4.202.54', icon: <Activity className="h-5 w-5 text-sky-400" />,     color: 'sky' },
  be4:    { label: 'BE-4',    role: 'LiveKit cluster #2 HA', ip: '10.4.202.55', icon: <Activity className="h-5 w-5 text-sky-400" />,     color: 'sky' },
  db2:    { label: 'DB-2',    role: 'PostgreSQL replica',    ip: '10.4.202.56', icon: <Database className="h-5 w-5 text-teal-400" />,    color: 'teal' },
  redis1: { label: 'REDIS-1', role: 'Redis dedicato',        ip: '10.4.202.57', icon: <HardDrive className="h-5 w-5 text-rose-400" />,   color: 'rose' },
};

interface ServiceDef { name: string; displayName: string; description: string; controllable: boolean; server?: 'be1' | 'be2' }
const SERVER_SERVICES: Record<ServerKey, ServiceDef[]> = {
  fe1: [
    { name: 'nginx', displayName: 'Nginx', description: 'Load balancer / reverse proxy + serve frontend tenant + master', controllable: false }
  ],
  be1: [
    { name: 'voip-backend', displayName: 'Node.js (PM2 fork)', description: 'Backend primario con FreeSWITCH ESL', controllable: true, server: 'be1' },
    { name: 'freeswitch', displayName: 'FreeSWITCH', description: 'Telephony engine', controllable: true, server: 'be1' },
  ],
  be2: [
    { name: 'voip-backend', displayName: 'Node.js (PM2 x4)', description: 'API · Webhook · CRM', controllable: true, server: 'be2' }
  ],
  db1: [
    { name: 'postgresql', displayName: 'PostgreSQL 16', description: 'Database primary (LAN only) + replica source per DB-2', controllable: false }
  ],
  be3: [
    { name: 'livekit-server', displayName: 'LiveKit server', description: 'SFU/TURN nodo 1 cluster Video (gestito via SSH)', controllable: false },
    { name: 'livekit-egress', displayName: 'Egress worker', description: 'Recording rooms (gestito via SSH)', controllable: false },
    { name: 'livekit-agent', displayName: 'Agent worker', description: 'Trascrizione live (gestito via SSH)', controllable: false },
  ],
  be4: [
    { name: 'livekit-server', displayName: 'LiveKit server', description: 'SFU/TURN nodo 2 HA cluster Video (gestito via SSH)', controllable: false },
    { name: 'livekit-egress', displayName: 'Egress worker', description: 'Recording rooms (gestito via SSH)', controllable: false },
  ],
  db2: [
    { name: 'postgresql', displayName: 'PostgreSQL 16 replica', description: 'Hot standby di DB-1 via streaming replication (LAN only)', controllable: false }
  ],
  redis1: [
    { name: 'redis-server', displayName: 'Redis dedicato', description: 'Cache + BullMQ + Socket.IO adapter (LAN only, gestito via SSH)', controllable: false }
  ],
};

function formatBytes(bytes: number): string {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return `${days}d ${hours}h ${mins}m`;
}

function getHealthColor(pct: number): string {
  if (pct < 70) return 'text-emerald-400';
  if (pct < 85) return 'text-amber-400';
  return 'text-rose-400';
}

function getHealthBadge(pct: number): React.ReactNode {
  if (pct < 70) return <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400">Ottimale</Badge>;
  if (pct < 85) return <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400">Attenzione</Badge>;
  return <Badge className="bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-400">Critico</Badge>;
}

function statusDot(status: string) {
  // 'not_configured' (PM-024): nodo senza env LAN nel backend → grigio, non rosso.
  const c = status === 'healthy' ? 'bg-emerald-400' : status === 'degraded' ? 'bg-amber-400' : status === 'loading' ? 'bg-slate-300 animate-pulse' : status === 'not_configured' ? 'bg-slate-300 ring-1 ring-slate-400' : 'bg-rose-500';
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${c} mr-1.5`} />;
}

export default function SystemMonitoring() {
  const { toast } = useToast();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [selectedServer, setSelectedServer] = useState<ServerKey>('be1');
  const [infra, setInfra] = useState<InfraHealth | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryPoint[]>([]);

  const loadInfra = useCallback(async () => {
    try {
      const res = await apiClient.get('/master/infrastructure/health');
      if (res.success && res.data) {
        setInfra(res.data as InfraHealth);
        const s = (res.data as InfraHealth).servers;
        const be1 = s?.be1;
        if (be1?.cpu) {
          setHistory(prev => {
            const point: HistoryPoint = {
              time: new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
              cpu: be1.cpu?.usage ?? 0,
              memory: be1.memory?.percentage ?? 0
            };
            return [...prev, point].slice(-30);
          });
        }
      }
    } catch (err) {
      console.error('Error loading infrastructure health:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading || !isAuthenticated) return;
    void loadInfra();
    const iv = setInterval(loadInfra, 15000);
    return () => clearInterval(iv);
  }, [authLoading, isAuthenticated, loadInfra]);

  const manualRefresh = async () => {
    setRefreshing(true);
    await loadInfra();
    setRefreshing(false);
    toast({ title: 'Aggiornato', description: 'Metriche infrastruttura aggiornate' });
  };

  const handleServiceAction = async (serviceName: string, action: ServiceAction, server?: 'be1' | 'be2') => {
    const key = `${serviceName}-${action}`;
    setActionLoading(key);
    try {
      const data = await apiClient.post('/master/security/system/service-action', {
        service: serviceName,
        action,
        ...(server ? { server } : {})
      });
      if (data.success) {
        toast({ title: 'Successo', description: `${serviceName} ${action} completato` });
      } else {
        throw new Error(data.error || 'Azione fallita');
      }
    } catch (err: unknown) {
      toast({ title: 'Errore', description: toErr(err).message || 'Operazione fallita', variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto mb-4" />
          <p className="text-muted-foreground dark:text-muted-foreground/70">Caricamento infrastruttura...</p>
        </div>
      </div>
    );
  }

  const servers = infra?.servers;
  const sel = servers?.[selectedServer];
  const meta = SERVER_LABELS[selectedServer];
  const services = SERVER_SERVICES[selectedServer];

  const getCpuPct = () => sel?.cpu?.usage ?? 0;
  const getMemPct = () => sel?.memory?.percentage ?? 0;
  const getDiskPct = () => sel?.disk?.percentage ?? 0;
  const getUptime = () => sel?.uptime ?? 0;

  const getServiceRunning = (serviceName: string): boolean => {
    if (!sel) return false;
    if (selectedServer === 'be1') {
      if (serviceName === 'freeswitch') return sel.freeswitch?.connected === true;
      return sel.status === 'healthy' || sel.status === 'degraded';
    }
    if (selectedServer === 'be2') return sel.status === 'healthy' || sel.status === 'degraded';
    if (selectedServer === 'fe1') return sel.nginx?.status === 'healthy';
    if (selectedServer === 'db1') return sel.status === 'healthy';
    if (selectedServer === 'be3' || selectedServer === 'be4') return sel.status === 'healthy';
    if (selectedServer === 'db2') return sel.status === 'healthy' || sel.status === 'degraded';
    if (selectedServer === 'redis1') return sel.status === 'healthy' && sel.ping === 'PONG';
    return false;
  };

  const ServiceStatusBadge = ({ serviceName }: { serviceName: string }) => {
    const running = getServiceRunning(serviceName);
    if (running) {
      return (
        <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400">
          <CheckCircle2 className="h-3 w-3 mr-1" /> Running
        </Badge>
      );
    }
    return (
      <Badge className="bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-400">
        <AlertCircle className="h-3 w-3 mr-1" /> Down
      </Badge>
    );
  };

  return (
    <div className="min-h-screen p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <Server className="h-8 w-8 text-cyan-500 dark:text-cyan-400" />
            Infrastructure Monitoring
          </h1>
          <p className="text-muted-foreground dark:text-muted-foreground/70 mt-1">
            Cluster 8 nodi: FE-1 · BE-1 · BE-2 · DB-1 · BE-3 · BE-4 · DB-2 · REDIS-1
            {infra && (
              <span className="ml-3">
                {statusDot(infra.overall)}
                <span className={`font-medium capitalize ${infra.overall === 'healthy' ? 'text-emerald-400' : infra.overall === 'degraded' ? 'text-amber-400' : 'text-rose-400'}`}>
                  {infra.overall}
                </span>
              </span>
            )}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={manualRefresh} disabled={refreshing}
          className="border-border dark:border-white/20 hover:bg-muted">
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Aggiorna
        </Button>
      </div>

      {/* Server Overview Tabs */}
      <GlassCard variant="elevated">
        <GlassCardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            {(Object.keys(SERVER_LABELS) as ServerKey[]).map(key => {
              const m = SERVER_LABELS[key];
              const s = servers?.[key];
              const status = s?.status || 'loading';
              const isSelected = selectedServer === key;
              return (
                <button
                  key={key}
                  onClick={() => setSelectedServer(key)}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                    isSelected
                      ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-500/10'
                      : 'border-border dark:border-white/10 hover:border-border dark:hover:border-white/20 bg-white dark:bg-white/5'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    {m.icon}
                    <span className="font-bold text-sm text-foreground">{m.label}</span>
                  </div>
                  <p className="text-xs text-muted-foreground dark:text-muted-foreground/70 mb-2">{m.role}</p>
                  <div className="flex items-center">
                    {statusDot(status)}
                    <span className={`text-xs font-medium capitalize ${
                      status === 'healthy' ? 'text-emerald-600 dark:text-emerald-400' :
                      status === 'degraded' ? 'text-amber-600 dark:text-amber-400' :
                      status === 'loading' ? 'text-muted-foreground' : 'text-rose-600 dark:text-rose-400'
                    }`}>{status}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </GlassCardContent>
      </GlassCard>

      {/* Selected Server Detail */}
      <div className="flex items-center gap-3">
        {meta.icon}
        <div>
          <h2 className="text-xl font-bold text-foreground">{meta.label} — {meta.role}</h2>
          <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">LAN: {meta.ip}</p>
        </div>
        <Badge className="ml-2 bg-muted text-muted-foreground font-mono text-xs">{meta.ip}</Badge>
      </div>

      {/* BE-3 / BE-4 — LiveKit cluster node */}
      {(selectedServer === 'be3' || selectedServer === 'be4') && (
        <GlassCard variant="elevated">
          <GlassCardHeader className="p-4 pb-3">
            <GlassCardTitle className="flex items-center gap-2 text-base">
              <Activity className="h-5 w-5 text-sky-400" />
              LiveKit cluster — {meta.label}
            </GlassCardTitle>
            <GlassCardDescription>
              Endpoint pubblico WebRTC/SFU: <span className="font-mono">wss://media.easyflowai.it</span>
            </GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent className="p-4 pt-0">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-muted/50 dark:bg-white/5 rounded-lg border border-border dark:border-white/10">
                <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Stato nodo</p>
                <p className="text-lg font-semibold capitalize">
                  {statusDot(sel?.status || 'loading')}
                  <span className={sel?.status === 'healthy' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}>
                    {sel?.status || '—'}
                  </span>
                </p>
              </div>
              <div className="p-4 bg-muted/50 dark:bg-white/5 rounded-lg border border-border dark:border-white/10">
                <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Probe</p>
                <p className="text-lg font-semibold text-foreground uppercase">{sel?.probe ?? '—'}</p>
              </div>
              <div className="p-4 bg-muted/50 dark:bg-white/5 rounded-lg border border-border dark:border-white/10">
                <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Latenza</p>
                <p className="text-2xl font-bold text-sky-600 dark:text-sky-400">
                  {sel?.latency_ms != null ? `${sel.latency_ms}ms` : '—'}
                </p>
              </div>
              <div className="p-4 bg-muted/50 dark:bg-white/5 rounded-lg border border-border dark:border-white/10">
                <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Porta</p>
                <p className="text-2xl font-bold text-foreground">{sel?.port ?? 7880}</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground dark:text-muted-foreground/70 mt-3">{sel?.detail ?? '—'}</p>
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
              Vincolo: il motore Video gira ESCLUSIVAMENTE su BE-3/BE-4. Nessun deploy LiveKit su FE-1/BE-1/BE-2/DB-*.
            </p>
          </GlassCardContent>
        </GlassCard>
      )}

      {/* DB-2 — replica */}
      {selectedServer === 'db2' && (
        <GlassCard variant="elevated">
          <GlassCardHeader className="p-4 pb-3">
            <GlassCardTitle className="flex items-center gap-2 text-base">
              <Database className="h-5 w-5 text-teal-400" />
              PostgreSQL replica
            </GlassCardTitle>
          </GlassCardHeader>
          <GlassCardContent className="p-4 pt-0">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-muted/50 dark:bg-white/5 rounded-lg border border-border dark:border-white/10">
                <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Stato</p>
                <p className="text-lg font-semibold capitalize">
                  {statusDot(sel?.status || 'loading')}
                  <span className={sel?.status === 'healthy' ? 'text-emerald-600 dark:text-emerald-400' : sel?.status === 'degraded' ? 'text-amber-600 dark:text-amber-400' : 'text-rose-600 dark:text-rose-400'}>
                    {sel?.status || '—'}
                  </span>
                </p>
              </div>
              <div className="p-4 bg-muted/50 dark:bg-white/5 rounded-lg border border-border dark:border-white/10">
                <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Replication state</p>
                <p className="text-lg font-semibold text-foreground">{sel?.state ?? '—'}</p>
              </div>
              <div className="p-4 bg-muted/50 dark:bg-white/5 rounded-lg border border-border dark:border-white/10">
                <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Sync mode</p>
                <p className="text-lg font-semibold text-foreground">{sel?.sync_state ?? '—'}</p>
              </div>
              <div className="p-4 bg-muted/50 dark:bg-white/5 rounded-lg border border-border dark:border-white/10">
                <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Lag WAL</p>
                <p className="text-2xl font-bold text-teal-600 dark:text-teal-400">
                  {sel?.lag_bytes != null ? `${(sel.lag_bytes / 1024).toFixed(1)} KB` : '—'}
                </p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground dark:text-muted-foreground/70 mt-3">{sel?.detail ?? '—'}</p>
            <p className="text-xs text-muted-foreground/70 dark:text-muted-foreground mt-2">
              DB-2 è hot standby di DB-1 via streaming replication. Stato letto da <span className="font-mono">pg_stat_replication</span> sul primary.
            </p>
          </GlassCardContent>
        </GlassCard>
      )}

      {/* REDIS-1 — Redis dedicato */}
      {selectedServer === 'redis1' && (
        <GlassCard variant="elevated">
          <GlassCardHeader className="p-4 pb-3">
            <GlassCardTitle className="flex items-center gap-2 text-base">
              <HardDrive className="h-5 w-5 text-rose-400" />
              Redis dedicato
            </GlassCardTitle>
            <GlassCardDescription>Cache + BullMQ + Socket.IO adapter (LAN only)</GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent className="p-4 pt-0">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-muted/50 dark:bg-white/5 rounded-lg border border-border dark:border-white/10">
                <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Stato</p>
                <p className="text-lg font-semibold capitalize">
                  {statusDot(sel?.status || 'loading')}
                  <span className={sel?.status === 'healthy' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}>
                    {sel?.status || '—'}
                  </span>
                </p>
              </div>
              <div className="p-4 bg-muted/50 dark:bg-white/5 rounded-lg border border-border dark:border-white/10">
                <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">PING</p>
                <p className={`text-2xl font-bold ${sel?.ping === 'PONG' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                  {sel?.ping ?? '—'}
                </p>
              </div>
              <div className="p-4 bg-muted/50 dark:bg-white/5 rounded-lg border border-border dark:border-white/10">
                <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Latenza</p>
                <p className="text-2xl font-bold text-rose-600 dark:text-rose-400">
                  {sel?.latency_ms != null ? `${sel.latency_ms}ms` : '—'}
                </p>
              </div>
              <div className="p-4 bg-muted/50 dark:bg-white/5 rounded-lg border border-border dark:border-white/10">
                <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Versione</p>
                <p className="text-lg font-semibold text-foreground">{sel?.version ?? '—'}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <div className="p-4 bg-muted/50 dark:bg-white/5 rounded-lg border border-border dark:border-white/10">
                <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Memoria usata</p>
                <p className="text-lg font-semibold text-foreground">{sel?.used_memory_human ?? '—'}</p>
                {sel?.used_memory_peak_human && (
                  <p className="text-xs text-muted-foreground dark:text-muted-foreground">peak {sel.used_memory_peak_human}</p>
                )}
              </div>
              <div className="p-4 bg-muted/50 dark:bg-white/5 rounded-lg border border-border dark:border-white/10">
                <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Frammentazione</p>
                <p className={`text-lg font-semibold ${
                  sel?.fragmentation_ratio != null && sel.fragmentation_ratio > 2.0 ? 'text-rose-600 dark:text-rose-400'
                    : sel?.fragmentation_ratio != null && sel.fragmentation_ratio > 1.5 ? 'text-amber-600 dark:text-amber-400'
                    : 'text-emerald-600 dark:text-emerald-400'
                }`}>
                  {sel?.fragmentation_ratio != null ? sel.fragmentation_ratio.toFixed(2) : '—'}
                </p>
              </div>
              <div className="p-4 bg-muted/50 dark:bg-white/5 rounded-lg border border-border dark:border-white/10">
                <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Client connessi</p>
                <p className="text-lg font-semibold text-foreground">{sel?.connected_clients ?? '—'}</p>
              </div>
              <div className="p-4 bg-muted/50 dark:bg-white/5 rounded-lg border border-border dark:border-white/10">
                <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Client bloccati</p>
                <p className={`text-lg font-semibold ${sel?.blocked_clients ? 'text-amber-600 dark:text-amber-400' : 'text-foreground'}`}>
                  {sel?.blocked_clients ?? '—'}
                </p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground dark:text-muted-foreground/70 mt-3">{sel?.detail ?? '—'}</p>
          </GlassCardContent>
        </GlassCard>
      )}

      {/* Resource Metrics */}
      {(selectedServer === 'fe1' || selectedServer === 'be1' || selectedServer === 'be2') && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <GlassCard variant="interactive">
            <GlassCardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Cpu className="h-5 w-5 text-cyan-500 dark:text-cyan-400" />
                  <span className="font-semibold text-foreground">CPU</span>
                </div>
                {getHealthBadge(getCpuPct())}
              </div>
              <div className={`text-3xl font-bold mb-2 ${getHealthColor(getCpuPct())}`}>
                {getCpuPct().toFixed(1)}%
              </div>
              <Progress value={getCpuPct()} className="h-2 bg-muted dark:bg-white/10" />
              <p className="text-xs text-muted-foreground dark:text-muted-foreground/70 mt-2">
                {sel?.cpu?.cores ?? '?'} cores · load {sel?.cpu?.loadAvg?.[0]?.toFixed(2) ?? '?'}
              </p>
            </GlassCardContent>
          </GlassCard>

          <GlassCard variant="interactive">
            <GlassCardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <MemoryStick className="h-5 w-5 text-purple-500 dark:text-purple-400" />
                  <span className="font-semibold text-foreground">RAM</span>
                </div>
                {getHealthBadge(getMemPct())}
              </div>
              <div className={`text-3xl font-bold mb-2 ${getHealthColor(getMemPct())}`}>
                {getMemPct().toFixed(1)}%
              </div>
              <Progress value={getMemPct()} className="h-2 bg-muted dark:bg-white/10" />
              <p className="text-xs text-muted-foreground dark:text-muted-foreground/70 mt-2">
                {formatBytes(sel?.memory?.used ?? 0)} / {formatBytes(sel?.memory?.total ?? 0)}
              </p>
            </GlassCardContent>
          </GlassCard>

          <GlassCard variant="interactive">
            <GlassCardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <HardDrive className="h-5 w-5 text-amber-500 dark:text-amber-400" />
                  <span className="font-semibold text-foreground">Disco</span>
                </div>
                {getHealthBadge(getDiskPct())}
              </div>
              <div className={`text-3xl font-bold mb-2 ${getHealthColor(getDiskPct())}`}>
                {getDiskPct().toFixed(1)}%
              </div>
              <Progress value={getDiskPct()} className="h-2 bg-muted dark:bg-white/10" />
              <p className="text-xs text-muted-foreground dark:text-muted-foreground/70 mt-2">
                {formatBytes(sel?.disk?.free ?? 0)} liberi
              </p>
            </GlassCardContent>
          </GlassCard>

          <GlassCard variant="interactive">
            <GlassCardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-emerald-500 dark:text-emerald-400" />
                  <span className="font-semibold text-foreground">Uptime</span>
                </div>
                <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400">Online</Badge>
              </div>
              <div className="text-2xl font-bold text-foreground mb-2">
                {getUptime() ? formatUptime(getUptime()) : '—'}
              </div>
              <p className="text-xs text-muted-foreground dark:text-muted-foreground/70 mt-4">Server attivo</p>
            </GlassCardContent>
          </GlassCard>
        </div>
      )}

      {/* FE-1 specific: Nginx stats */}
      {selectedServer === 'fe1' && (
        <GlassCard variant="elevated">
          <GlassCardHeader className="p-4 pb-3">
            <GlassCardTitle className="flex items-center gap-2 text-base">
              <Network className="h-5 w-5 text-cyan-400" />
              Nginx Status
            </GlassCardTitle>
          </GlassCardHeader>
          <GlassCardContent className="p-4 pt-0">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-muted/50 dark:bg-white/5 rounded-lg border border-border dark:border-white/10">
                <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Connessioni attive</p>
                <p className="text-2xl font-bold text-cyan-600 dark:text-cyan-400">
                  {sel?.nginx?.active_connections ?? '—'}
                </p>
              </div>
              <div className="p-4 bg-muted/50 dark:bg-white/5 rounded-lg border border-border dark:border-white/10">
                <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Stato</p>
                <p className="text-lg font-semibold text-foreground capitalize">
                  {statusDot(sel?.nginx?.status || 'unreachable')}
                  {sel?.nginx?.status || 'Sconosciuto'}
                </p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground/70 dark:text-muted-foreground mt-3">
              Nginx è su FE-1 — il restart avviene via gestione diretta SSH, non via API backend.
            </p>
          </GlassCardContent>
        </GlassCard>
      )}

      {/* DB-1 specific: Database stats */}
      {selectedServer === 'db1' && (
        <GlassCard variant="elevated">
          <GlassCardHeader className="p-4 pb-3">
            <GlassCardTitle className="flex items-center gap-2 text-base">
              <Database className="h-5 w-5 text-emerald-400" />
              PostgreSQL Status
            </GlassCardTitle>
          </GlassCardHeader>
          <GlassCardContent className="p-4 pt-0">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-muted/50 dark:bg-white/5 rounded-lg border border-border dark:border-white/10">
                <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Dimensione DB</p>
                <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                  {sel?.db_size_gb ?? '—'} GB
                </p>
              </div>
              <div className="p-4 bg-muted/50 dark:bg-white/5 rounded-lg border border-border dark:border-white/10">
                <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Connessioni attive</p>
                <p className="text-2xl font-bold text-foreground">
                  {sel?.active_connections ?? '—'}
                </p>
              </div>
              <div className="p-4 bg-muted/50 dark:bg-white/5 rounded-lg border border-border dark:border-white/10">
                <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Query latency</p>
                <p className="text-2xl font-bold text-foreground">
                  {sel?.query_latency_ms != null ? `${sel.query_latency_ms}ms` : '—'}
                </p>
              </div>
              <div className="p-4 bg-muted/50 dark:bg-white/5 rounded-lg border border-border dark:border-white/10">
                <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Stato</p>
                <p className="text-lg font-semibold">
                  {statusDot(sel?.status || 'loading')}
                  <span className={`capitalize ${sel?.status === 'healthy' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                    {sel?.status || '—'}
                  </span>
                </p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground/70 dark:text-muted-foreground mt-3">
              DB-1 è accessibile solo via LAN (10.4.202.53:5432). Il restart avviene via SSH diretto, non via API.
            </p>
          </GlassCardContent>
        </GlassCard>
      )}

      {/* BE-1 specific: FreeSWITCH + Redis client (verso REDIS-1) */}
      {selectedServer === 'be1' && (
        <GlassCard variant="elevated">
          <GlassCardHeader className="p-4 pb-3">
            <GlassCardTitle className="flex items-center gap-2 text-base">
              <Zap className="h-5 w-5 text-amber-400" />
              FreeSWITCH & client Redis
            </GlassCardTitle>
            <GlassCardDescription>Il broker Redis è REDIS-1 (10.4.202.57): vedi tab dedicato per stato server.</GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent className="p-4 pt-0">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-muted/50 dark:bg-white/5 rounded-lg border border-border dark:border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <Activity className="h-4 w-4 text-amber-400" />
                  <span className="font-semibold text-sm text-foreground">FreeSWITCH ESL</span>
                </div>
                <div className="flex items-center">
                  {statusDot(sel?.freeswitch?.connected ? 'healthy' : 'critical')}
                  <span className="text-sm font-medium text-foreground dark:text-foreground">
                    {sel?.freeswitch?.state || 'Sconosciuto'}
                  </span>
                </div>
                {sel?.freeswitch?.latency_ms && (
                  <p className="text-xs text-muted-foreground/70 dark:text-muted-foreground mt-1">Latenza ESL: {sel.freeswitch.latency_ms}ms</p>
                )}
              </div>
              <div className="p-4 bg-muted/50 dark:bg-white/5 rounded-lg border border-border dark:border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <Database className="h-4 w-4 text-rose-400" />
                  <span className="font-semibold text-sm text-foreground">Client Redis → REDIS-1</span>
                </div>
                <div className="flex items-center">
                  {statusDot(sel?.redis?.connected ? 'healthy' : 'critical')}
                  <span className="text-sm font-medium text-foreground dark:text-foreground">
                    {sel?.redis?.connected ? 'Connesso' : 'Disconnesso'}
                  </span>
                </div>
                {sel?.redis?.latency_ms && (
                  <p className="text-xs text-muted-foreground/70 dark:text-muted-foreground mt-1">RTT: {sel.redis.latency_ms}ms</p>
                )}
              </div>
            </div>
          </GlassCardContent>
        </GlassCard>
      )}

      {/* History Chart (solo BE-1 e BE-2) */}
      {(selectedServer === 'be1' || selectedServer === 'be2') && history.length > 1 && (
        <GlassCard variant="elevated">
          <GlassCardHeader className="p-4 pb-3">
            <GlassCardTitle className="flex items-center gap-2 text-base">
              <Activity className="h-5 w-5 text-cyan-400" />
              CPU & RAM — {meta.label} (30 rilevamenti)
            </GlassCardTitle>
            <GlassCardDescription>Aggiornamento ogni 15 secondi</GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent className="p-4 pt-0">
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={history}>
                <defs>
                  <linearGradient id="colorCpu" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#38bdf8" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#38bdf8" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorMemory" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#a855f7" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#a855f7" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200 dark:stroke-white/10" />
                <XAxis dataKey="time" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ backgroundColor: 'rgba(15,23,42,0.9)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }} />
                <Legend />
                <Area type="monotone" dataKey="cpu" stroke="#38bdf8" fillOpacity={1} fill="url(#colorCpu)" name="CPU %" />
                <Area type="monotone" dataKey="memory" stroke="#a855f7" fillOpacity={1} fill="url(#colorMemory)" name="RAM %" />
              </AreaChart>
            </ResponsiveContainer>
          </GlassCardContent>
        </GlassCard>
      )}

      {/* Services Management */}
      <GlassCard variant="elevated">
        <GlassCardHeader className="p-4 pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Server className="h-5 w-5 text-purple-400" />
              <GlassCardTitle className="text-base">Servizi — {meta.label}</GlassCardTitle>
            </div>
            <GlassCardDescription>Gestione servizi su {meta.label}</GlassCardDescription>
          </div>
        </GlassCardHeader>
        <GlassCardContent className="p-4 pt-0">
          <div className="space-y-3">
            {services.map(service => (
              <div key={service.name}
                className="flex flex-wrap items-center justify-between gap-4 p-4 bg-muted/50 dark:bg-white/5 border border-border dark:border-white/10 rounded-lg">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h4 className="font-semibold text-foreground">{service.displayName}</h4>
                    <ServiceStatusBadge serviceName={service.name} />
                  </div>
                  <p className="text-sm text-muted-foreground dark:text-muted-foreground/70 mt-1">{service.description}</p>
                </div>
                {service.controllable ? (
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm"
                      onClick={() => handleServiceAction(service.name, 'stop', service.server)}
                      disabled={actionLoading !== null}
                      className="gap-2 text-rose-400 border-rose-500/30 hover:bg-rose-500/10">
                      {actionLoading === `${service.name}-stop` ? <div className="animate-spin h-4 w-4 border-2 border-rose-400 border-t-transparent rounded-full" /> : <Square className="h-4 w-4" />}
                      Stop
                    </Button>
                    <Button variant="outline" size="sm"
                      onClick={() => handleServiceAction(service.name, 'restart', service.server)}
                      disabled={actionLoading !== null}
                      className="gap-2 text-cyan-400 border-cyan-500/30 hover:bg-cyan-500/10">
                      {actionLoading === `${service.name}-restart` ? <div className="animate-spin h-4 w-4 border-2 border-cyan-400 border-t-transparent rounded-full" /> : <RotateCw className="h-4 w-4" />}
                      Restart
                    </Button>
                  </div>
                ) : (
                  <Badge className="bg-muted text-muted-foreground text-xs">
                    Gestito via SSH
                  </Badge>
                )}
              </div>
            ))}
          </div>
          {!services.some(s => s.controllable) && (
            <p className="text-xs text-muted-foreground/70 dark:text-muted-foreground mt-3 text-center">
              I servizi di {meta.label} richiedono accesso SSH diretto per la gestione.
            </p>
          )}
        </GlassCardContent>
      </GlassCard>

      {/* Footer info */}
      <div className="text-center text-xs text-muted-foreground/70 dark:text-muted-foreground pb-2">
        Architettura distribuita — subnet 10.4.202.0/24 — aggiornamento ogni 15 secondi
        {infra?.timestamp && ` — Ultimo update: ${new Date(infra.timestamp).toLocaleTimeString('it-IT')}`}
      </div>
    </div>
  );
}
